import classNames from 'classnames';
import React from 'react';
import { MenuItem } from '../../components/Menu';
import OverflowMenu from '../../components/OverflowMenu';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import editIcon from '../../icons/non-prefilled/16x16/editPost.svg';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import { sanitizeAnnotationLink } from '../../utils/url/sanitizeUrl';
import MagneticWallSheet from '../MagneticWallSheet';
import {
  ANNOTATION_CREATION_ID,
  usePendingMagneticWallItem,
} from '../PendingMagneticWallItemProvider';
import useMagneticWallSheetContext from '../useMagneticWallSheetContext';
import AnnotationCreate from './AnnotationCreate';
import AnnotationIcon from './AnnotationIcon';
import AnnotationLink from './AnnotationLink';
import AnnotationPreview from './AnnotationPreview';
import * as styles from './AnnotationSheet.module.scss';
import useAnnotationState from './useAnnotationState';

const ANNOTATION_FRAGMENT = gql(`
  fragment AnnotationSheet_annotation on Annotation {
    id
    ...AnnotationPreview_annotation
    ...useAnnotationState_annotation
  }
`);

interface AnnotationSheetProps {
  annotation: DocumentType<typeof ANNOTATION_FRAGMENT>;
  onClose: () => void;
  canEdit: boolean;
  createAnnotation: React.ComponentProps<
    typeof AnnotationCreate
  >['createAnnotation'];
  deleteAnnotation: () => void;
}

const AnnotationSheet = ({
  onClose,
  annotation,
  canEdit,
  createAnnotation,
  deleteAnnotation,
}: AnnotationSheetProps) => {
  const isNewAnnotation = annotation.id === ANNOTATION_CREATION_ID;

  const {
    annotationState,
    updateAnnotationState,
    isEditing,
    cancelEditing,
    startEditing,
    finishEditing,
  } = useAnnotationState(annotation);
  const trimmedLinkText = annotationState.linkText.trim();
  const sanitizedLink = sanitizeAnnotationLink(annotationState.link);

  const { setPendingAnnotationRange } = usePendingMagneticWallItem();

  const onDelete = () => {
    deleteAnnotation();
    onClose();
  };

  const { highlighted } = useMagneticWallSheetContext({
    type: 'annotation',
    id: annotation.id,
  });

  return (
    <MagneticWallSheet
      title={isNewAnnotation ? 'New annotation' : 'Annotation'}
      onClose={() => {
        onClose();
        setPendingAnnotationRange(null);
      }}
      headerContent={
        <>
          {highlighted && highlighted.length > 0 && (
            <div
              className={classNames(styles.contextContainer, {
                [styles.hasPreview]: !isEditing,
              })}
            >
              {highlighted}
            </div>
          )}
          {!isEditing && (
            <div className={styles.previewContainer}>
              <h5>Annotation</h5>
              <AnnotationPreview
                annotation={annotation}
                avatar={<AnnotationIcon />}
              />
            </div>
          )}
          {!isEditing && annotation.link && (
            <div className={styles.linkContainer}>
              <AnnotationLink
                link={annotation.link}
                text={annotation.linkText ?? ''}
              />
            </div>
          )}
        </>
      }
      overflowMenu={
        !isEditing && canEdit ? (
          <OverflowMenu>
            <MenuItem icon={editIcon} onClick={startEditing}>
              Edit annotation
            </MenuItem>
            <MenuItem icon={trashIcon} danger onClick={onDelete}>
              Delete annotation
            </MenuItem>
          </OverflowMenu>
        ) : null
      }
      inputRow={
        isEditing ? (
          <>
            {sanitizedLink && (
              <AnnotationLink link={sanitizedLink} text={trimmedLinkText} />
            )}
            <div className={styles.createContainer}>
              <AnnotationCreate
                annotationId={annotation.id}
                annotationState={annotationState}
                updateAnnotationState={updateAnnotationState}
                onSaved={finishEditing}
                onCancel={cancelEditing}
                createAnnotation={createAnnotation}
              />
            </div>
          </>
        ) : null
      }
    />
  );
};

export default AnnotationSheet;
